import React, { ChangeEvent, FC, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";

import {
  AnimalFragment,
  useGetAnimalsPageLazyQuery,
  useGroupsInfoQuery,
  useFieldNamesQuery,
  GetAnimalsPageQueryVariables,
  useExportAnimalsAsyncMutation,
  InputMaybe,
  OrderBy,
  AnimalWithTagFragment,
} from "generated/graphql";

import cn from "classnames";
import { CellProps, Column } from "react-table";
import { DateTime } from "luxon";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";
import { TablePaginationSizes, TABLE_PAGINATION_DEFAULT_OPTIONS } from "components/Common/Table/TablePagination";

import { TableOptionsContext } from "config/tableOptionsProvider";
import { getAnimalBreedsAsString, getDaysOnFarm, getFieldName, getMonthsAge } from "helpers/myLivestock";
import { getBUFromLocalStorage } from "helpers/storage";
import { getFormattedNumber, notNothing, removeNothings } from "helpers/general";
import { areAllRowsSelected, getPageTo, getPageFrom, parseSortRule } from "./helpers";
import { useFormatDate, useGetCurrentBusinessUnit, useOutsideClick, useStateSpecies } from "hooks";

import TableActionColumn from "pages/MyLivestock/Animals/TableActionColumn";
import { AnimalActions } from "animals/AnimalActions";
import { AnimalPregnancyLabel, AnimalsPerformancePill, AnimalsSexClassLabel } from "components/MyLivestock";
import { BaseInput } from "components/Common/Field/BaseInput";
import {
  Button,
  EmptyState,
  FieldDateRange,
  FieldSelect,
  Flex,
  Icon,
  InputCheckbox,
  LoadingOverlay,
  Pill,
  Spacer,
  Table,
  TableHeaderCaptions,
  Text,
} from "components";
import { COLUMN_IDS, renderStatus } from "components/MyLivestock/Animals/AnimalsTable";
import { SelectedAnimalsTable } from "animals/SelectedAnimalsList";
import { TABLE_IDS } from "constants/Interface";
import { Checkbox, getActionsColumn, TablePaginationTarget } from "components/Common/Table";
import { ExportAnimalsButton } from "components/MyLivestock/Animals/AnimalFiltersBlock/ExportAnimalsButton";
import { Filters, PregnancyStatus, Props, Sex } from "./types";

import filterStyles from "components/MyLivestock/Animals/AnimalFiltersBlock/AnimalFiltersBlock.module.scss";
import paginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import styles from "./AnimalsList.module.scss";
import { useRecoilState, useSetRecoilState } from "recoil";
import { asyncJobMenuState, asyncJobTaskState } from "state/asyncJobState";
import { Tag } from "tags/components/Tag";
import { useTags } from "tags/hooks/useTagsHook";
import { InputSelectOption } from "components/Common/Field/Select";

const tableId = TABLE_IDS["ANIMAL_LIST_SERVER"];

const INITIAL_FILTERS: Filters = {
  fieldId: "",
  groupId: "",
  tagIds: [],
  sex: "",
  pregnancyStatus: "",
  dateOfBirth: {
    from: undefined,
    to: undefined,
  },
  dateOnFarm: {
    from: undefined,
    to: undefined,
  },
};

export const AnimalsList: FC<Props> = ({
  fromPage,
  isArchive = false,
  fieldId,
  groupId,
  showSelectColumn,
  showAnimalActionButtons,
  showIndividualAnimalActionButtons = true,
  excludedFilters,
}) => {
  const businessUnitId = getBUFromLocalStorage();
  const [pageNumber, setPageNumber] = useState(1);
  const history = useHistory();
  const { getTableOptions, setTableOptions } = useContext(TableOptionsContext);
  const [sortBy, setSortBy] = useState<Array<string>>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSelectedAnimalsView, setIsSelectedAnimalsView] = useState(false);
  const { getFormatDate } = useFormatDate();
  const { weightUnits } = useGetCurrentBusinessUnit();
  const { active: speciesActive } = useStateSpecies();
  const animalTypeId = speciesActive?.id ? parseInt(speciesActive?.id) : undefined;

  const [exportCsv] = useExportAnimalsAsyncMutation();
  const [taskId, setTaskId] = useRecoilState(asyncJobTaskState);
  const setAsyncJobMenuState = useSetRecoilState(asyncJobMenuState);

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { data: fieldsData } = useFieldNamesQuery({
    variables: {
      businessUnitId,
    },
  });
  const fieldOptions = removeNothings(fieldsData?.fields ?? []);

  const { animalTags } = useTags();

  const animalTagOptions: InputSelectOption[] = React.useMemo(
    () =>
      animalTags.map((item) => {
        return { value: item?.node?.id || "", id: item?.node?.id, label: item?.node?.name, key: item?.node?.id || "" };
      }),
    [animalTags],
  );

  const { data: groupsData } = useGroupsInfoQuery({
    variables: {
      businessUnitId,
      animalTypeIds: animalTypeId && fromPage !== "field" ? [animalTypeId] : undefined,
    },
  });

  const groups = removeNothings(groupsData?.groupsExtended?.groups ?? []);
  const numberOfGroups = groupsData?.groupsExtended?.groupsCount;

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const tableOptions = getTableOptions<AnimalFragment>(tableId);
  const sortByValue = tableOptions?.sortBy;
  const selectedRowIds = tableOptions?.selectedRowsIds;

  const selectedAnimalIds = useMemo(() => (selectedRowIds ? [...selectedRowIds] : []), [selectedRowIds]);

  const pageSizeMenuRef = useRef(null);
  const [pageSize, setPageSize] = useState<TablePaginationSizes>(15);
  const [showPageSizeOptionsMenu, setShowPageSizeOptionsMenu] = useState<boolean>(false);

  const togglePageSizeOptionsMenu = (): void => {
    setShowPageSizeOptionsMenu((state) => !state);
  };

  const hidePageSizeOptionsMenu = (): void => {
    setShowPageSizeOptionsMenu(false);
  };

  const handleChangePageSize = (
    pageSize: TablePaginationSizes,
    event: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>,
  ): void => {
    event.stopPropagation();

    setPageNumber(1);
    setPageSize(pageSize);
    hidePageSizeOptionsMenu();
  };

  useOutsideClick(pageSizeMenuRef, hidePageSizeOptionsMenu);

  useEffect(() => {
    if (sortByValue) {
      setPageNumber(1);
      setSortBy(parseSortRule(sortByValue));
    }
  }, [sortByValue]);

  const [getAnimals, { data, loading }] = useGetAnimalsPageLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  useEffect(() => {
    const variables: GetAnimalsPageQueryVariables = {
      businessUnit: businessUnitId,
      pageSize,
      pageNumber,
      sortBy,
      search: searchTerm,
      animalTypeId: fromPage === "field" ? undefined : animalTypeId,
      group: groupId || filters.groupId || undefined,
      field: fieldId || filters.fieldId || undefined,
      animalTags: filters.tagIds.length > 0 ? filters.tagIds : undefined,
      isMale: filters.sex === Sex.MALE ? true : filters.sex === Sex.FEMALE ? false : undefined,
      isPregnant:
        filters.pregnancyStatus === PregnancyStatus.PREGNANT
          ? true
          : filters.pregnancyStatus === PregnancyStatus.NOTPREGNANT
          ? false
          : undefined,
      minDob: filters.dateOfBirth.from || undefined,
      maxDob: filters.dateOfBirth.to || undefined,
      minDof: filters.dateOnFarm.from || undefined,
      maxDof: filters.dateOnFarm.to || undefined,
      fetchRemoved: isArchive,
    };
    getAnimals({
      variables,
    });
  }, [
    pageNumber,
    sortBy,
    searchTerm,
    groupId,
    fieldId,
    businessUnitId,
    animalTypeId,
    getAnimals,
    pageSize,
    filters.fieldId,
    filters.groupId,
    filters.sex,
    filters.pregnancyStatus,
    filters.dateOfBirth.from,
    filters.dateOfBirth.to,
    filters.dateOnFarm.from,
    filters.dateOnFarm.to,
    fromPage,
    isArchive,
    filters.tagIds,
  ]);

  const animals = useMemo(() => data?.animalsPage?.animals?.filter(notNothing) ?? [], [data]);
  const hasPrev = data?.animalsPage?.hasPrev ?? false;
  const hasNext = data?.animalsPage?.hasNext ?? false;

  const onCellClick = useCallback(
    (animal: AnimalFragment) => {
      history.push(`/system/${fromPage || "livestock"}/animal/${animal.id}`);
    },
    [history, fromPage],
  );

  const handleAnimalsExport = async (animalIds: string[], orderBy?: InputMaybe<Array<OrderBy>>) => {
    const { data } = await exportCsv({
      variables: {
        input: {
          animals: animalIds,
          businessUnit: getBUFromLocalStorage(),
          isArchived: isArchive,
          orderBy: orderBy,
          animalTypeIds: speciesActive ? [+speciesActive.id] : null,
        },
      },
    });
    setAsyncJobMenuState("expanded");
    setTaskId(data?.exportAnimalsAsync?.export?.id);
  };

  const totalNumberOfAnimals = data?.animalsPage?.count ?? 0;
  const pageIndex = pageNumber - 1;
  const pageFrom = getPageFrom({ totalNumberOfAnimals, pageSize, pageIndex });
  const pageTo = getPageTo({ totalNumberOfAnimals, pageSize, pageFrom });

  //@ts-expect-error
  const columns: Column<AnimalFragment>[] = useMemo(
    () => [
      {
        id: COLUMN_IDS.SELECTION,
        Header: function SelectAllHeader({ data }: { data: AnimalFragment[] }) {
          const animalIds: Array<string> = data.map((a) => a.id);
          const selectedRowsIds = tableOptions?.selectedRowsIds;
          const checked = areAllRowsSelected(animalIds, selectedRowsIds ?? undefined);

          const updatedRows = new Set(
            Array.from(selectedRowsIds ?? []).filter((rowId) => {
              return !animalIds.includes(rowId);
            }),
          );

          const handleCheckboxChange = (): void => {
            if (checked) {
              setTableOptions({
                id: tableId,
                selectedRowsIds: updatedRows,
              });
            } else {
              const ids = new Set<string>(selectedRowsIds);

              data.forEach((item) => {
                ids.add(item["id"]);
              });

              setTableOptions({
                id: tableId,
                selectedRowsIds: ids,
              });
            }
          };
          return showSelectColumn ? <InputCheckbox checked={checked} onChange={handleCheckboxChange} /> : null;
        },
        Cell: function SelectColumn(
          props: React.PropsWithChildren<CellProps<AnimalFragment, string | null | undefined>>,
        ): React.ReactNode {
          const id = props.cell.row.original.id;

          return showSelectColumn ? <Checkbox tableId={tableId} id={id} /> : null;
        },
        sticky: "left",
        minWidth: showSelectColumn ? 40 : 0,
        width: showSelectColumn ? 40 : 0,
      },
      {
        id: COLUMN_IDS.ANIMAL_ID,
        Header: "Animal ID",
        accessor: "passportNumber",
        onCellClick,
        sticky: "left",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.E_ID,
        Header: "EID",
        accessor: "eId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.E_ID] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.VISUAL_ID,
        Header: "Visual ID",
        accessor: "visualId",
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.VISUAL_ID] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.BREED,
        Header: "Breed",
        accessor: (row): string => getAnimalBreedsAsString(row.animalBreeds),
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.BREED] || "\u2014",
        minWidth: 150,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.TAGS,
        Header: "Tags",
        accessor: "tags",
        onCellClick,
        Cell: function TagsCell({ row }) {
          const { tags } = row?.original;

          return tags.map((tag) => {
            const t = {
              tagId: tag.tagId,
              name: tag.name,
              color: tag.color,
              ownership: tag?.schema?.ownership,
            };
            return <Tag key={tag.tagId} tag={t} />;
          });
        },
        minWidth: 100,
        width: 150,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.SEX,
        Header: "Sex",
        accessor: ({ isMale, sexClassification }): string => sexClassification?.slug || (isMale ? "M" : "F"),
        onCellClick,
        Cell: function SexCell({ row }): React.ReactNode {
          const { id, isCastrated, isMale, sexClassification } = row?.original;

          return (
            <AnimalsSexClassLabel
              id={`animal_sex_label__${id}`}
              isCastrated={isCastrated}
              isMale={isMale}
              label={sexClassification?.title || null}
            />
          );
        },
        minWidth: 100,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.AGE,
        Header: <TableHeaderCaptions title="Age" subtitle="Months" />,
        accessor: ({ dateLeftFarm, deadAt, dob }): number | null => {
          // if dead, calculate age at death
          if (deadAt) return getMonthsAge(dob, deadAt);

          // if off farm, calculate age at move off farm
          if (dateLeftFarm) return getMonthsAge(dob, dateLeftFarm);

          // otherwise just calculate current age
          return getMonthsAge(dob);
        },
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.AGE] || "\u2014",
        minWidth: 100,
        width: 100,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DATE_OF_BIRTH,
        Header: "DoB",
        accessor: "dob",
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DATE_OF_BIRTH] ? getFormatDate(row.values[COLUMN_IDS.DATE_OF_BIRTH]) : "\u2014",
        onCellClick,
        minWidth: 120,
        width: 120,
      },
      {
        id: COLUMN_IDS.DATE_ON_FARM,
        Header: "Date on farm",
        accessor: "dateMovedToFarm",
        onCellClick,
        Cell: ({ value }): React.ReactNode => (value ? getFormatDate(value) : "\u2014"),
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DATE_MOVED_TO_FARM,
        Header: <TableHeaderCaptions title="Time on farm" subtitle="Days" />,
        accessor: ({ dateMovedToFarm }): number | null => (dateMovedToFarm ? getDaysOnFarm(dateMovedToFarm) : null),
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DATE_MOVED_TO_FARM] ? `${row.values[COLUMN_IDS.DATE_MOVED_TO_FARM]} days` : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.CURRENT_WEIGHT,
        Header: <TableHeaderCaptions title="Current weight" subtitle="Estimated" />,
        accessor: ({ currentWeight }): number | null => (currentWeight ? +currentWeight?.toFixed(1) : null),
        onCellClick,
        Cell: function CurrentWeightCell({ row }): React.ReactNode {
          return !!row.values[COLUMN_IDS.CURRENT_WEIGHT] ? (
            <AnimalsPerformancePill
              label={`${row.values[COLUMN_IDS.CURRENT_WEIGHT]} ${weightUnits}`}
              performanceCategory={row?.original?.performanceCategory}
            />
          ) : (
            "\u2014"
          );
        },
        minWidth: 160,
        width: 160,
      },
      {
        id: COLUMN_IDS.LAST_WEIGHT,
        Header: "Last Weight",
        accessor: ({ lastWeight }): number | null => lastWeight?.value || null,
        Cell: function LastWeightCell({ row }): React.ReactNode {
          return !!row.values[COLUMN_IDS.LAST_WEIGHT] ? (
            <Pill caption={`${getFormattedNumber(row.values[COLUMN_IDS.LAST_WEIGHT])} ${weightUnits}`} colour="grey" />
          ) : (
            "\u2014"
          );
        },
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DAYS_SINCE_LAST_WEIGHT,
        Header: "Days since weighed",
        accessor: ({ lastWeight }): number | null =>
          lastWeight?.date ? Math.trunc(DateTime.local().diff(DateTime.fromISO(lastWeight.date), ["days"]).days) : null,
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.DAYS_SINCE_LAST_WEIGHT] || "\u2014",
        minWidth: 120,
        width: 120,
        disableSortBy: true,
      },
      {
        id: COLUMN_IDS.DLWG,
        Header: "DLWG",
        accessor: ({ growthRate }): string | null => growthRate?.toFixed(1) || null,
        onCellClick,
        Cell: ({ row }): React.ReactNode => row.values[COLUMN_IDS.DLWG] || "\u2014",
        minWidth: 100,
        width: 100,
      },
      {
        id: COLUMN_IDS.PREGNANCY_STATUS,
        Header: <TableHeaderCaptions title="Pregnancy" subtitle="Status" />,
        accessor: "isPregnant",
        onCellClick,
        Cell: function PregnancyStatusCell({ row: { original } }): React.ReactNode {
          return !original?.isMale && original?.isPregnant ? (
            <AnimalPregnancyLabel
              dueDate={original?.pregnancyDueDate}
              id={`animal_pregnancy_label__${original?.id}`}
              isMale={original?.isMale}
              isPregnant={!!original?.isPregnant}
            />
          ) : (
            "\u2014"
          );
        },
        minWidth: 120,
        width: 120,
      },
      {
        id: COLUMN_IDS.DELIVERY_DATE,
        Header: "Delivery date",
        accessor: "deliveryDate",
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.DELIVERY_DATE] ? getFormatDate(row.values[COLUMN_IDS.DELIVERY_DATE]) : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.GROUP,
        Header: "Group",
        accessor: ({ group }): string | null => group?.name || null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.GROUP] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.FIELD,
        Header: "Location",
        accessor: ({ field }): string | null =>
          field ? getFieldName({ fieldName: field.name, unitName: field.location?.unitName }) : null,
        onCellClick,
        Cell: ({ row }): React.ReactElement => row.values[COLUMN_IDS.FIELD] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.LAST_SYNCED,
        Header: "Last Synced",
        accessor: "lastRegulatorySyncedDate",
        onCellClick,
        Cell: ({ row }): React.ReactNode =>
          row.values[COLUMN_IDS.LAST_SYNCED] ? getFormatDate(row.values[COLUMN_IDS.LAST_SYNCED]) : "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.STATUS,
        Header: "Status",
        sticky: "right",
        onCellClick,
        accessor: (animal): JSX.Element => {
          return renderStatus(
            { isRegulatorySynced: animal.isRegulatorySynced, withdrawalEnd: animal.withdrawalEnd },
            { isArchive, isCarcass: undefined },
            getFormatDate,
          );
        },
        disableSortBy: true,
        minWidth: isArchive ? 75 : 100,
        width: isArchive ? 75 : 100,
      },
      ...(showIndividualAnimalActionButtons
        ? [
            {
              ...getActionsColumn<AnimalFragment>(),
              Cell: function ActionColumn({ row: { original } }): JSX.Element | null {
                return (
                  <TableActionColumn
                    animal={original}
                    group={undefined}
                    isFarmWithGroups={numberOfGroups ? numberOfGroups > 0 : false}
                  />
                );
              },
              sticky: "right",
            },
          ]
        : []),
    ],
    [
      onCellClick,
      isArchive,
      tableOptions?.selectedRowsIds,
      setTableOptions,
      getFormatDate,
      weightUnits,
      numberOfGroups,
      showSelectColumn,
      showIndividualAnimalActionButtons,
    ],
  );

  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  const debouncedSearchTerm = useMemo(() => {
    return debounce(onChangeSearchTerm, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearchTerm.cancel();
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    return () => {
      setTableOptions({ selectedRows: undefined, selectedRowsIds: undefined, id: tableId });
    };
  }, [setTableOptions]);

  useEffect(() => {
    setTableOptions({ selectedRows: undefined, selectedRowsIds: undefined, id: tableId });
  }, [setTableOptions, speciesActive]);

  useEffect(() => {
    if (isSelectedAnimalsView && !selectedAnimalIds.length) {
      setIsSelectedAnimalsView(false);
    }
  }, [selectedAnimalIds, isSelectedAnimalsView]);

  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  const caption = `${selectedAnimalIds?.length} Selected - View selection`;

  const animalsData = useMemo(
    () => (loading && animals.length === 0 ? getLoadingStateAnimals(pageSize) : animals),
    [animals, loading, pageSize],
  );

  if (!speciesActive) {
    return <LoadingOverlay />;
  }

  if (isSelectedAnimalsView) {
    return (
      <>
        <Flex item itemGutter>
          <Spacer baselineHeight={1} />

          {!!selectedAnimalIds.length && showAnimalActionButtons && <AnimalActions animalIds={selectedAnimalIds} />}
          <Spacer baselineHeight={1} />

          <Flex
            className={paginationStyles.table_pagination_target}
            container
            containerAlignItems="center"
            containerJustifyContent="flex-end"
            containerWrap="wrap"
          >
            <Button
              caption="Back to list"
              colour="grey"
              variant="hollow"
              onClick={() => setIsSelectedAnimalsView(false)}
            />
            <Flex item itemGrow />
            <TablePaginationTarget tableId={TABLE_IDS["ANIMAL_LIST_SERVER_SELECTED"]} />
            <div style={{ width: "10px" }} />
            <Flex>
              <ExportAnimalsButton
                isLoading={!!taskId}
                numAnimals={selectedRowIds?.size || 0}
                onClick={() => handleAnimalsExport(selectedAnimalIds, sortByValue)}
                isServerList
                animalStatus="selected"
              />
            </Flex>
          </Flex>
        </Flex>

        <Spacer baselineHeight={1} />

        <Flex item itemGutter xs="fill">
          {/* @ts-expect-error */}
          <SelectedAnimalsTable columns={columns} sourceTableId={tableId} />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex item itemGutter>
        <Spacer baselineHeight={1} />

        {!!selectedAnimalIds.length && showAnimalActionButtons && <AnimalActions animalIds={selectedAnimalIds} />}

        <Spacer baselineHeight={1} />

        <Flex container containerJustifyContent="space-between">
          <Flex container>
            <Flex className={styles.search_input} item>
              <BaseInput type="text" onChange={debouncedSearchTerm} name="animal_search" placeholder="Search by ID" />
            </Flex>

            {showSelectColumn && Boolean(selectedAnimalIds.length) && (
              <Flex item itemGutter>
                <Button caption={caption} onClick={() => setIsSelectedAnimalsView((state) => !state)} colour="green" />
              </Flex>
            )}
          </Flex>

          <Flex container containerAlignItems="center">
            <Flex item container>
              <button
                className={cn(
                  paginationStyles.table_pagination__button,
                  paginationStyles["table_pagination__button--first"],
                )}
                disabled={!hasPrev}
                onClick={(): void => setPageNumber(1)}
                title={`First page`}
              >
                <Icon name="doubleChevron" rotate={180} size="atom" />
              </button>

              <button
                className={cn(
                  paginationStyles.table_pagination__button,
                  paginationStyles["table_pagination__button--prev"],
                )}
                title="Previous page"
                onClick={() => setPageNumber((pageNumber) => pageNumber - 1)}
                disabled={!hasPrev}
              >
                <Icon name="chevron" rotate={180} size="atom" />
              </button>
            </Flex>

            <Flex item>
              <>
                <div className={paginationStyles.table_pagination__per_page} ref={pageSizeMenuRef}>
                  <button
                    className={cn(paginationStyles.table_pagination__current_page, {
                      [paginationStyles["table_pagination__current_page--active"]]: showPageSizeOptionsMenu,
                    })}
                    type="button"
                    onClick={togglePageSizeOptionsMenu}
                    title="Rows per page"
                  >
                    <Flex container>
                      <Flex item itemGutter>
                        {pageFrom} to {pageTo}
                      </Flex>
                      <Flex item>
                        <Icon name="rectangle" size="atom" colour="grey_dark" />
                      </Flex>
                    </Flex>
                  </button>

                  {showPageSizeOptionsMenu && (
                    <div className={paginationStyles.table_pagination__page_sizes}>
                      <span className={paginationStyles.table_pagination__page_sizes_title}>Rows:</span>

                      {TABLE_PAGINATION_DEFAULT_OPTIONS.SIZES.map((size) => (
                        <span
                          className={cn(paginationStyles.table_pagination__page_size, {
                            [paginationStyles["table_pagination__page_size--active"]]: size === pageSize,
                          })}
                          key={size}
                          onClick={(event): void => handleChangePageSize(size, event)}
                          onKeyPress={(event): void => handleChangePageSize(size, event)}
                          tabIndex={0}
                        >
                          {size}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <span className={paginationStyles.table_pagination__total_items}>of {totalNumberOfAnimals}</span>
              </>
            </Flex>

            <Flex item container>
              <button
                title="Next page"
                onClick={() => setPageNumber((pageNumber) => pageNumber + 1)}
                disabled={!hasNext}
                className={cn(
                  paginationStyles.table_pagination__button,
                  paginationStyles["table_pagination__button--next"],
                )}
              >
                <Icon name="chevron" size="atom" />
              </button>
              <button
                className={cn(
                  paginationStyles.table_pagination__button,
                  paginationStyles["table_pagination__button--last"],
                )}
                disabled={!hasNext}
                onClick={(): void => setPageNumber(data?.animalsPage?.pages ?? 1)}
                title={`Last page`}
              >
                <Icon name="doubleChevron" size="atom" />
              </button>
            </Flex>
            <div style={{ width: 10 }} />
            <Flex>
              <ExportAnimalsButton
                isLoading={!!taskId}
                numAnimals={selectedRowIds?.size || totalNumberOfAnimals}
                onClick={() => handleAnimalsExport([], sortByValue)}
                isServerList
                animalStatus={isArchive ? "archived" : "on-farm"}
              />
            </Flex>

            <div style={{ width: 10 }} />

            <button
              className={cn(filterStyles.toggle_button, {
                [filterStyles["toggle_button--active"]]: showFilters,
              })}
              onClick={() => setShowFilters((prevState) => !prevState)}
            >
              <Text smaller>Filters</Text>

              <Icon className={filterStyles.toggle_button__icon} name="filter" colour="black" size="small" />
            </button>
          </Flex>
        </Flex>
      </Flex>

      {/* Filters block */}
      {showFilters && (
        <Flex item itemGutter>
          <Flex className={cn(styles.filtersContainer)} container>
            {
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label="Tags"
                  inputProps={{
                    name: "tags",
                    value: filters.tagIds,
                    multiple: true,
                    options: animalTagOptions,
                    placeholder: "No preference",
                    onChange: (event) => {
                      setFilters({
                        ...filters,
                        tagIds: event.target.value,
                      });
                    },
                    isClearable: true,
                  }}
                />
              </Flex>
            }
            {!excludedFilters?.groupId && (
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label="Group"
                  inputProps={{
                    name: "groups",
                    value: groups.find((group) => group.id === filters.groupId)?.name ?? "",
                    multiple: false,
                    options: groups,
                    placeholder: "No preference",
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      setFilters({ ...filters, groupId: event.target.value }),
                    labelKey: "name",
                    valueKey: "id",
                    isClearable: true,
                  }}
                />
              </Flex>
            )}
            {!excludedFilters?.fieldId && (
              <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
                <FieldSelect
                  label={"Location"}
                  inputProps={{
                    name: "fields",
                    value: fieldOptions.find((field) => field.id === filters.fieldId)?.name ?? "",
                    multiple: false,
                    options: fieldOptions,
                    placeholder: "No preference",
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      setFilters({ ...filters, fieldId: event.target.value }),
                    labelKey: "name",
                    valueKey: "id",
                    isClearable: true,
                  }}
                />
              </Flex>
            )}
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Sex"
                inputProps={{
                  name: "sex",
                  value: filters.sex ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: Sex.MALE,
                      label: "Male",
                    },
                    {
                      key: 2,
                      value: Sex.FEMALE,
                      label: "Female",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, sex: event.target.value }),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={3} xl={2}>
              <FieldSelect
                label="Pregnancy status"
                inputProps={{
                  name: "pregnancyStatus",
                  value: filters.pregnancyStatus ?? "",
                  placeholder: "No preference",
                  options: [
                    {
                      key: 1,
                      value: PregnancyStatus.PREGNANT,
                      label: "Pregnant",
                    },
                    {
                      key: 2,
                      value: PregnancyStatus.NOTPREGNANT,
                      label: "Not pregnant",
                    },
                  ],
                  onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setFilters({ ...filters, pregnancyStatus: event.target.value }),
                  isClearable: true,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label="DoB range"
                inputProps={{
                  from: {
                    value: filters.dateOfBirth.from,
                    name: "dobDateFrom",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOfBirth: { ...filters.dateOfBirth, from: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOfBirth.to,
                    name: "dobDateTo",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOfBirth: { ...filters.dateOfBirth, to: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} m={6} xl={4}>
              <FieldDateRange
                label="Date on farm range"
                inputProps={{
                  from: {
                    value: filters.dateOnFarm.from,
                    name: "dofDateFrom",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOnFarm: { ...filters.dateOnFarm, from: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                  to: {
                    value: filters.dateOnFarm.to,
                    name: "dofDateTo",
                    onChange: (date: Date) =>
                      setFilters({ ...filters, dateOnFarm: { ...filters.dateOnFarm, to: date } }),
                    placeholderText: "No preference",
                    isClearable: true,
                  },
                }}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
      {/* End of filters block */}

      <Spacer baselineHeight={1} />

      {animals.length === 0 && !loading ? (
        <EmptyState page="ANIMALS" />
      ) : (
        <Flex container item itemGrow>
          <Flex item itemGutter xs="fill">
            {loading && <LoadingOverlay />}
            <Table<AnimalFragment>
              data={animalsData}
              isAnimals={!(fromPage === "field")}
              columns={columns}
              tableId={tableId}
              manualSortBy={true}
              manualPagination={true}
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

export const getLoadingStateAnimals = (numberOfRows: TablePaginationSizes) => {
  const animal: AnimalFragment | AnimalWithTagFragment = {
    id: "",
    tags: [],
    passportNumber: "—",
    isCastrated: false,
    isMale: false,
    animalBreeds: [],
  };

  return Array.from({ length: numberOfRows }, () => animal);
};
